import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Button,
  InputCheckbox,
  IconButton,
  HeaderText,
  BodyText,
  CloseIcon,
} from 'shared-portal-component';

import SearchInput from '@/components/molecules/SearchInput/SearchInput';
import { selectDraft, selectInternalUsers } from '@/store/selectors/createSix';
import TwoSlot from '@/components/molecules/TwoSlot/TwoSlot';
import '@/components/organism/CreateSix/steps/style.scss';
import { setDraftField, setStep } from '@/store/slices/createSix/createSixSlice';
import { useAppDispatch } from '@/store';
import { MIN_LEN } from '@/constants';

const StepInternalParticipants: React.FC = () => {
  const users = useSelector(selectInternalUsers);
  const dispatch = useAppDispatch();
  const draft = useSelector(selectDraft);

  // Exclude users already selected as owners
  const availableUsers = users.filter(
    (user) => !draft.ownerIds.includes(user.id)
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(draft.internalParticipantIds);

  const handleCheckboxChange = (userId: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const handleRemoveSelected = (userId: string) => {
    setSelectedIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleProceed = () => {
    dispatch(setDraftField({ key: 'internalParticipantIds', value: selectedIds }));
    dispatch(setStep('ExternalParticipants'));
  };

  const handleBack = () => dispatch(setStep('Owners'));

  return (
    <Container fluid>
      <Row spacing="md" justify="start">
        <Col md={6}>
          <SearchInput />
        </Col>
      </Row>

      <TwoSlot>
        {/* LEFT: Available internal users */}
        <TwoSlot.FirstSlot>
          <Container fluid>
            <Row spacing="none" justify="start" align="center">
              <Col size={1}>""</Col>
              <Col size={5}>
                <HeaderText className="header-text" text="Name" as="h1" size="h1" />
              </Col>
              <Col size={5}>
                <HeaderText className="header-text" text="Email" as="h1" size="h1" />
              </Col>
            </Row>
          </Container>

          <Container fluid className="user-list-scroll">
            {availableUsers.map((user) => (
              <Row key={user.id} spacing="none" align="center">
                <Col size={6}>
                  <InputCheckbox
                    checked={selectedIds.includes(user.id)}
                    label={
                      <BodyText className="body-text" as="p">
                        {user.name}
                      </BodyText>
                    }
                    onChange={(checked: boolean) =>
                      { handleCheckboxChange(user.id, checked); }
                    }
                    id={`user-checkbox-${user.id}`}
                  />
                </Col>
                <Col size={6}>
                  <BodyText className="body-text" as="p">
                    {user.email}
                  </BodyText>
                </Col>
              </Row>
            ))}
          </Container>
        </TwoSlot.FirstSlot>

        {/* RIGHT: Selected participants */}
        <TwoSlot.SecondSlot>
          <Container fluid>
            <div className="px-4">
              <HeaderText
                className="header-text"
                text="Selected SIX participants"
                as="h1"
                size="h1"
              />
              {selectedIds.length === MIN_LEN ? (
                <div>No participants selected</div>
              ) : (
                selectedIds.map((id) => {
                  const user = availableUsers.find((user) => user.id === id);
                  if (user === undefined) return null;
                  return (
                    <Row spacing="none" key={id} align="center">
                      <IconButton
                        icon={<CloseIcon size="1x" />}
                        size="md"
                        ariaLabel="Remove"
                        variant="secondary"
                        onClick={() => { handleRemoveSelected(id); }}
                      />
                      <BodyText className="body-text" as="p">
                        {user.name} <span>{user.email}</span>
                      </BodyText>
                    </Row>
                  );
                })
              )}
            </div>
          </Container>
        </TwoSlot.SecondSlot>
      </TwoSlot>

      {/* Navigation */}
      <Row spacing="md" justify="center">
        <Button text="Go Back" variant="primary" size="md" onClick={handleBack} />
        <Button
          text="Proceed"
          variant="primary"
          size="md"
          onClick={handleProceed}
        />
      </Row>
    </Container>
  );
};

export default StepInternalParticipants;
